import { enqueueSnackbar } from 'notistack'
import { FormEvent, useEffect, useMemo, useState } from 'react'
import { useApolloClients } from 'lib/apollo/apolloProvider'
import { TPatientStatusModalProps } from './PatientStatusModal.types'
import { useUpdatePatientStatusMutation } from 'lib/apollo/hooks'
import { TPatientStatusOption } from 'lib/services/restApi/organizations/organizations.types'
import { Option } from 'common/types'
import { EPatientStatus } from 'views/RoomView/RoomView.types'
import { useRoomViewStore } from 'store'

export const usePatientStatusModalLogic = (props: TPatientStatusModalProps) => {
  const { surgeries, surgery, onClose, refetch } = props
  const { iservicesClient: client } = useApolloClients()

  const patientStatusOptions: (Option & { index: number })[] = JSON.parse(
    localStorage.getItem('patientStatusOptions') || '[]'
  ).map((option: TPatientStatusOption) => ({
    id: option.status,
    name: option.value,
    index: option.id,
  }))

  const { rooms } = useRoomViewStore()

  const locationTreeOptions = useMemo(() => {
    const options = rooms

    const occupiedRoomIds = new Set<number>()

    surgeries.forEach((surgery) => {
      const roomId = surgery?.patientStatusHistory?.[0]?.info?.room?.id
      const patientStatus = surgery?.patientStatusHistory?.[0]?.status

      // Only consider room occupied if patient is not checked out
      if (roomId && patientStatus !== EPatientStatus.CHECKED_OUT) {
        occupiedRoomIds.add(roomId)
      }
    })

    const unoccupiedRooms = options.filter(
      (option: any) => !occupiedRoomIds.has(option.id)
    )
    const occupiedRooms = options.filter((option: any) =>
      occupiedRoomIds.has(option.id)
    )

    return [
      ...unoccupiedRooms.map((option: any) => ({
        ...option,
        dotColor: 'green',
      })),
      ...occupiedRooms.map((option: any) => ({
        ...option,
        dotColor: 'gray',
        disabled: true,
      })),
    ]
  }, [surgeries, rooms])

  const currentStatus = surgery?.patientStatusHistory?.[0]?.status
    ? (patientStatusOptions.find(
        (option: Option) =>
          option.id === surgery?.patientStatusHistory?.[0]?.status
      )?.id as string)
    : (patientStatusOptions?.[0]?.id as string)

  const changeStatus = surgery?.patientStatusHistory?.[0]?.status
    ? (() => {
        const foundOptionIndex =
          patientStatusOptions.find(
            (option: Option & { index: number }) =>
              option.id === surgery?.patientStatusHistory?.[0]?.status
          )?.index || 1
        return (
          (patientStatusOptions.find(
            (option) => option.index === foundOptionIndex + 1
          )?.id as string) || (patientStatusOptions?.[0]?.id as string)
        )
      })()
    : (patientStatusOptions.find(
        (option: Option & { index: number }) => option.index === 2
      )?.id as string) || (patientStatusOptions?.[0]?.id as string)

  const roomNumberId =
    changeStatus === EPatientStatus.ASSIGN_ROOM
      ? surgery?.patientStatusHistory?.[0]?.info?.room?.id
        ? surgery?.patientStatusHistory?.[0]?.info?.room?.id
        : locationTreeOptions?.[0]?.id
      : undefined

  const [formData, setFormData] = useState<{
    currentStatus: string
    changeStatus: string
    roomNumberId?: number
  }>({
    currentStatus,
    changeStatus,
    roomNumberId,
  })

  const [updatePatientStatus, { loading: isLoadingUpdatePatientStatus }] =
    useUpdatePatientStatusMutation(client)

  const [isRefetching, setIsRefetching] = useState(false)

  const isLoading = isLoadingUpdatePatientStatus || isRefetching

  const patientName = `${
    surgery?.patient?.lastName
  }, ${surgery?.patient?.firstName?.charAt(0)}`

  const handleChangeFormData = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    })
  }

  const handleSubmit = async (
    e: FormEvent<HTMLFormElement | HTMLButtonElement>
  ) => {
    e.preventDefault()

    try {
      await updatePatientStatus({
        variables: {
          procedureId: surgery?._id,
          patientStatus: formData.changeStatus,
          roomId: formData.roomNumberId,
        },
      })

      if (refetch) {
        setIsRefetching(true)
        try {
          await refetch()
        } finally {
          setIsRefetching(false)
        }
      }

      closeModal()
      enqueueSnackbar('Patient status updated successfully', {
        variant: 'success',
      })
    } catch (error: unknown) {
      let errorMessage = 'Could not update patient status'
      if (error instanceof Error) {
        errorMessage = error.message
      }
      enqueueSnackbar(errorMessage, { variant: 'error' })
    }
  }

  useEffect(() => {
    if (formData.changeStatus === EPatientStatus.ASSIGN_ROOM) {
      setFormData((prev) => ({
        ...prev,
        roomNumberId:
          surgery?.patientStatusHistory?.[0]?.status ===
          EPatientStatus.CHECKED_OUT
            ? locationTreeOptions?.[0]?.id
            : surgery?.patientStatusHistory?.[0]?.info?.room?.id
            ? surgery?.patientStatusHistory?.[0]?.info?.room?.id
            : locationTreeOptions?.[0]?.id,
      }))
    }
    if (formData.changeStatus !== EPatientStatus.ASSIGN_ROOM) {
      setFormData((prev) => ({
        ...prev,
        roomNumberId: undefined,
      }))
    }
    if (formData.changeStatus === EPatientStatus.CHECKED_OUT) {
      setFormData((prev) => ({
        ...prev,
        roomNumberId: surgery?.patientStatusHistory?.[0]?.info?.room?.id
          ? surgery?.patientStatusHistory?.[0]?.info?.room?.id
          : undefined,
      }))
    }
  }, [
    locationTreeOptions,
    formData.changeStatus,
    surgery?.patientStatusHistory,
  ])

  const closeModal = () =>
    onClose({
      show: false,
      surgery: null,
    })

  return {
    closeModal,
    handleSubmit,
    handleChangeFormData,
    formData,
    isLoading,
    patientName,
    locationTreeOptions,
    patientStatusOptions,
  }
}
